import { AnimationPlaybackControls, useAnimate } from 'motion/react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Custom hook that manages the toast timer and progress animation.
 * Handles pausing, resuming, and cleanup of the toast timer.
 */
export const useToastTimer = (duration: number, onClose: () => void) => {
  const [scope, animate] = useAnimate();
  const animationRef = useRef<AnimationPlaybackControls>(null);
  const [alreadyStarted, setAlreadyStarted] = useState(false);

  const startProgress = useCallback(async () => {
    if (duration <= 0 || !scope.current || alreadyStarted) return;

    animationRef.current = animate(
      scope.current,
      { scaleX: 0 },
      { duration: duration / 1000, ease: 'linear', onComplete: onClose, autoplay: true }
    );

    setAlreadyStarted(true);
  }, [duration, scope, alreadyStarted, animate, onClose]);

  useLayoutEffect(() => {
    startProgress();
  }, [startProgress]);

  return {
    scope,
    setIsPaused: (isPaused: boolean) => {
      if (isPaused) {
        animationRef.current?.pause();
      } else {
        animationRef.current?.play();
      }
    },
  };
};
